import Vue from 'vue';
import { canAccessSupervisao, hasLoginsWrite } from '@/lib/Permissions';

const initialState = () => ({
  supervisionados: [],
  modo: '',
  loaded: false,
  loading: false,
  logs: [],
  total: 0,
  paginaAtual: 1,
  totalPorPagina: 50,
  logsLoading: false,
  logsError: null,
  atividade: null,
  atividadeLoading: false,
  atividadeError: null,
  granularidadeAtividade: 'dia',
});

const state = initialState();

const getters = {
  getSupervisionados(state) {
    return state.supervisionados;
  },
  getModoSupervisao(state) {
    return state.modo;
  },
  isSupervisaoLoaded(state) {
    return state.loaded;
  },
  canAccessSupervisao(state, getters, rootState) {
    const permissions = rootState.autenticacao?.user?.permissoes || {};
    return canAccessSupervisao(permissions, state.supervisionados);
  },
  isSupervisaoAdmin(state, getters, rootState) {
    const permissions = rootState.autenticacao?.user?.permissoes || {};
    return hasLoginsWrite(permissions);
  },
  getLogsList(state) {
    return state.logs;
  },
  getPaginacao(state) {
    return {
      total: state.total,
      paginaAtual: state.paginaAtual,
      totalPorPagina: state.totalPorPagina,
      totalPaginas: Math.ceil(state.total / state.totalPorPagina) || 0,
    };
  },
  getLogsLoading(state) {
    return state.logsLoading;
  },
  getLogsError(state) {
    return state.logsError;
  },
  getAtividade(state) {
    return state.atividade;
  },
  getAtividadeLoading(state) {
    return state.atividadeLoading;
  },
  getAtividadeError(state) {
    return state.atividadeError;
  },
  getGranularidadeAtividade(state) {
    return state.granularidadeAtividade;
  },
};

const actions = {
  clearState({ commit }) {
    commit('RESET');
  },

  fetchSupervisionados({ commit, rootState }) {
    return new Promise(async (resolve) => {
      if (!rootState.autenticacao?.user) {
        commit('SET_SUPERVISIONADOS', { lista: [], modo: '' });
        commit('SET_LOADED', true);
        resolve({ status: 401, msg: 'Usuário não autenticado' });
        return;
      }

      commit('SET_LOADING', true);

      try {
        const api = new Vue.prototype.$httpClient();
        const response = await api.send('/logins/supervisionados', 'GET', null, undefined, false);

        if (response.status === 200) {
          const payload = response.data?.dados || {};
          const lista = payload.lista || (Array.isArray(payload) ? payload : []);
          const modo = payload.modo || '';
          commit('SET_SUPERVISIONADOS', { lista, modo });
          commit('SET_LOADED', true);
          resolve({ status: 200, msg: 'Supervisionados carregados', dados: { lista, modo } });
          return;
        }

        if (response.status === 403) {
          commit('SET_SUPERVISIONADOS', { lista: [], modo: '' });
          commit('SET_LOADED', true);
          resolve({ status: 403, msg: response.data?.mensagem || 'Sem permissão' });
          return;
        }

        commit('SET_SUPERVISIONADOS', { lista: [], modo: '' });
        commit('SET_LOADED', true);
        resolve({
          status: response.status,
          msg: response.data?.mensagem || 'Erro ao carregar supervisionados',
        });
      } catch (error) {
        console.error('[SUPERVISAO] Erro ao carregar supervisionados:', error);
        commit('SET_SUPERVISIONADOS', { lista: [], modo: '' });
        commit('SET_LOADED', true);
        resolve({ status: 500, msg: error.message || 'Erro interno' });
      } finally {
        commit('SET_LOADING', false);
      }
    });
  },

  fetchLogs({ commit }, filtros = {}) {
    return new Promise(async (resolve) => {
      commit('SET_LOGS_LOADING', true);
      commit('CLEAR_LOGS_ERROR');

      try {
        const api = new Vue.prototype.$httpClient();
        const params = new URLSearchParams();

        if (filtros.loginId) params.append('loginId', filtros.loginId);
        if (filtros.usuario) params.append('usuario', filtros.usuario);
        if (filtros.operacao) params.append('operacao', filtros.operacao);
        if (filtros.entidade) params.append('entidade', filtros.entidade);
        if (filtros.sucesso !== undefined && filtros.sucesso !== '') {
          params.append('sucesso', String(filtros.sucesso));
        }
        if (filtros.ip) params.append('ip', filtros.ip);
        if (filtros.dataInicio) params.append('dataInicio', filtros.dataInicio);
        if (filtros.dataFim) params.append('dataFim', filtros.dataFim);

        const pagina = filtros.pagina || 1;
        const totalPorPagina = filtros.totalPorPagina || 50;
        params.append('pagina', String(pagina));
        params.append('totalPorPagina', String(totalPorPagina));

        const url = `/logs/supervisao?${params.toString()}`;
        const response = await api.send(url, 'GET', null, undefined, false);

        if (response.status === 200) {
          const dados = response.data?.dados || {};
          commit('SET_LOGS', dados.logs || []);
          commit('SET_TOTAL', dados.paginacao?.total || 0);
          commit('SET_PAGINA_ATUAL', pagina);
          commit('SET_TOTAL_POR_PAGINA', totalPorPagina);
          resolve({ status: 200, msg: 'Logs recuperados', dados });
          return;
        }

        commit('SET_LOGS', []);
        commit('SET_TOTAL', 0);
        commit('SET_LOGS_ERROR', response.data?.mensagem || 'Erro ao buscar logs');
        resolve({
          status: response.status,
          msg: response.data?.mensagem || 'Erro ao buscar logs',
        });
      } catch (error) {
        console.error('[SUPERVISAO] Erro ao buscar logs:', error);
        commit('SET_LOGS', []);
        commit('SET_TOTAL', 0);
        commit('SET_LOGS_ERROR', error.message || 'Erro interno');
        resolve({ status: 500, msg: error.message || 'Erro interno' });
      } finally {
        commit('SET_LOGS_LOADING', false);
      }
    });
  },

  fetchAtividade({ commit, state }, filtros = {}) {
    return new Promise(async (resolve) => {
      commit('SET_ATIVIDADE_LOADING', true);
      commit('CLEAR_ATIVIDADE_ERROR');

      try {
        const api = new Vue.prototype.$httpClient();
        const params = new URLSearchParams();

        const granularidade = filtros.granularidade || state.granularidadeAtividade || 'dia';
        params.append('granularidade', granularidade);

        if (filtros.usuario) params.append('usuario', filtros.usuario);
        if (filtros.operacao) params.append('operacao', filtros.operacao);
        if (filtros.entidade) params.append('entidade', filtros.entidade);
        if (filtros.sucesso !== undefined && filtros.sucesso !== '') {
          params.append('sucesso', String(filtros.sucesso));
        }
        if (filtros.ip) params.append('ip', filtros.ip);
        if (filtros.dataInicio) params.append('dataInicio', filtros.dataInicio);
        if (filtros.dataFim) params.append('dataFim', filtros.dataFim);

        const url = `/logs/supervisao/atividade?${params.toString()}`;
        const response = await api.send(url, 'GET', null, undefined, false);

        if (response.status === 200) {
          const dados = response.data?.dados || {};
          commit('SET_ATIVIDADE', dados);
          commit('SET_GRANULARIDADE_ATIVIDADE', dados.granularidade || granularidade);
          resolve({ status: 200, msg: 'Atividade recuperada', dados });
          return;
        }

        commit('SET_ATIVIDADE', null);
        commit('SET_ATIVIDADE_ERROR', response.data?.mensagem || 'Erro ao buscar atividade');
        resolve({
          status: response.status,
          msg: response.data?.mensagem || 'Erro ao buscar atividade',
        });
      } catch (error) {
        console.error('[SUPERVISAO] Erro ao buscar atividade:', error);
        commit('SET_ATIVIDADE', null);
        commit('SET_ATIVIDADE_ERROR', error.message || 'Erro interno');
        resolve({ status: 500, msg: error.message || 'Erro interno' });
      } finally {
        commit('SET_ATIVIDADE_LOADING', false);
      }
    });
  },

  setGranularidadeAtividade({ commit }, granularidade) {
    commit('SET_GRANULARIDADE_ATIVIDADE', granularidade);
  },
};

const mutations = {
  RESET(state) {
    Object.assign(state, initialState());
  },
  SET_SUPERVISIONADOS(state, { lista, modo }) {
    state.supervisionados = lista || [];
    state.modo = modo || '';
  },
  SET_LOADED(state, loaded) {
    state.loaded = loaded;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_LOGS(state, logs) {
    state.logs = logs;
  },
  SET_TOTAL(state, total) {
    state.total = total;
  },
  SET_PAGINA_ATUAL(state, pagina) {
    state.paginaAtual = pagina;
  },
  SET_TOTAL_POR_PAGINA(state, totalPorPagina) {
    state.totalPorPagina = totalPorPagina;
  },
  SET_LOGS_LOADING(state, loading) {
    state.logsLoading = loading;
  },
  SET_LOGS_ERROR(state, error) {
    state.logsError = error;
  },
  CLEAR_LOGS_ERROR(state) {
    state.logsError = null;
  },
  SET_ATIVIDADE(state, atividade) {
    state.atividade = atividade;
  },
  SET_ATIVIDADE_LOADING(state, loading) {
    state.atividadeLoading = loading;
  },
  SET_ATIVIDADE_ERROR(state, error) {
    state.atividadeError = error;
  },
  CLEAR_ATIVIDADE_ERROR(state) {
    state.atividadeError = null;
  },
  SET_GRANULARIDADE_ATIVIDADE(state, granularidade) {
    state.granularidadeAtividade = granularidade;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
